import React, { ReactElement, ReactNode } from 'react'
import { storiesOf } from '@storybook/react'

import { ListItem, Typography, List, Box, makeStyles } from '@material-ui/core'

import { ScoreItemIcon } from 'components/PlayerLayer/components/PlayerProfile/components'
import { BonusScoreToken, MatchEventToken } from 'lib/enumTranslations'
import { BonusScoreType, MatchEventType } from 'protos'
import T from 'components/FormattedMessage'

const bonusScoreTypes = [
  BonusScoreType.BS_ACTIVITY_BONUS,
  BonusScoreType.BS_CLEAN_SHEET_BONUS,
  BonusScoreType.BS_CORNER,
  BonusScoreType.BS_GOAL,
  BonusScoreType.BS_SHOT_ON_GOAL,
  BonusScoreType.BS_TEAM_IN_DRAW_BONUS,
  BonusScoreType.BS_TEAM_IN_LEAD_BONUS,
  BonusScoreType.BS_UNKNOWN,
]

const matchEventTypes = [
  MatchEventType.ET_ADM_EXTRA_HALF_1_STARTED,
  MatchEventType.ET_ADM_EXTRA_HALF_2_STARTED,
  MatchEventType.ET_ADM_EXTRA_TIME_BREAK_STARTED,
  MatchEventType.ET_ADM_FULL_TIME,
  MatchEventType.ET_ADM_HALF_TIME_STARTED,
  MatchEventType.ET_ADM_MATCH_FINISHED,
  MatchEventType.ET_ADM_MATCH_STARTED,
  MatchEventType.ET_ADM_PENALTY_SHOOTOUT_STARTED,
  MatchEventType.ET_ADM_SECOND_HALF_STARTED,
  MatchEventType.ET_ADM_WAITING_FOR_PENALTY_SHOOTOUT,
  MatchEventType.ET_ASSIST,
  MatchEventType.ET_CORNER,
  MatchEventType.ET_GOAL,
  MatchEventType.ET_OWN_GOAL,
  MatchEventType.ET_PENALTY_CONCEDED,
  MatchEventType.ET_PENALTY_GOAL,
  MatchEventType.ET_PENALTY_MISSED,
  MatchEventType.ET_PENALTY_SAVED,
  MatchEventType.ET_PENALTY_SHOOTOUT_GOAL,
  MatchEventType.ET_PENALTY_SHOOTOUT_MISSED,
  MatchEventType.ET_RED_CARD,
  MatchEventType.ET_SAVE,
  MatchEventType.ET_SHOT_ON_GOAL,
  MatchEventType.ET_SUB_IN,
  MatchEventType.ET_SUB_OUT,
  MatchEventType.ET_UNKNOWN,
  MatchEventType.ET_YELLOW_CARD,
  MatchEventType.ET_YELLOW_RED_CARD,
]

const useStyles = makeStyles(({ spacing }) => {
  return {
    iconContainer: {
      width: 30,
      height: 30,
      marginRight: spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& img': {
        maxWidth: 24,
      },
    }
  }
})

type Props<T> = {
  values: T[]
  getToken: (value: T) => string
  renderIcon: (value: T) => ReactNode
}

type IGFC = <T>(props: Props<T>) => ReactElement<Props<T>>

const ScoreItemList: IGFC = ({ values, getToken, renderIcon }) => {
  const classes = useStyles()

  return (
    <Box bgcolor="accent.main" color="common.white">
      <List>
        {values.map((value, i) => {
          const token = getToken(value)

          return (
            <ListItem key={i}>
              <Box className={classes.iconContainer}>
                {renderIcon(value)}
              </Box>

              <Typography variant="body1" color="inherit">
                {token ? <T id={token} /> : '-'}
              </Typography>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}

storiesOf('ScoreItemIcon', module)
  .add('BonusScoreType', () => {
    return (
      <ScoreItemList
        values={bonusScoreTypes}
        getToken={BonusScoreToken}
        renderIcon={value => <ScoreItemIcon bonus={value} />}
      />
    )
  })
  .add('MatchEventType', () => {
    return (
      <ScoreItemList
        values={matchEventTypes}
        getToken={MatchEventToken}
        renderIcon={value => <ScoreItemIcon event={value} />}
      />
    )
  })
